// pages/secure/CommissionsPage.js
// Agent commission disbursements: compute from a closed transaction, then record
// income and pay the agent — each action books to the ledger automatically.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const STATUS = {
    draft: 'bg-slate-100 text-slate-600', approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700', void: 'bg-red-100 text-red-600',
};

const CommissionsPage = () => {
    const apiClient = useApiClient();
    const [rows, setRows] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [computing, setComputing] = useState(null);   // {transaction_id}
    const [paying, setPaying] = useState(null);         // disbursement row
    const [editing, setEditing] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        apiClient.get('/api/commissions')
            .then(setRows)
            .catch(err => setError(err.message));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);

    const openCompute = async () => {
        setError('');
        try {
            const txns = await apiClient.get('/api/transactions');
            // Prefer closed deals with a price + commission %
            setTransactions(txns);
            setComputing({ transaction_id: '' });
        } catch (err) { setError(err.message); }
    };

    const runCompute = async () => {
        setIsSaving(true);
        setError('');
        try {
            const r = await apiClient.post(`/api/commissions/compute/${computing.transaction_id}`);
            setComputing(null);
            setFlash(`Computed ${r.disbursements.length} disbursement(s) — GCI ${fmt(r.transaction_gci)}.`);
            setTimeout(() => setFlash(''), 4000);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const recordIncome = async (row) => {
        try { await apiClient.post(`/api/commissions/${row.disbursement_id}/record-income`); load(); }
        catch (err) { setError(err.message); }
    };

    const doPay = async () => {
        setIsSaving(true);
        setError('');
        try {
            await apiClient.post(`/api/commissions/${paying.disbursement_id}/pay`, { check_number: paying.check_number, paid_date: paying.paid_date });
            setPaying(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const saveEdit = async () => {
        setIsSaving(true);
        setError('');
        try {
            await apiClient.put(`/api/commissions/${editing.disbursement_id}`, {
                gross_commission: editing.gross_commission, split_percentage: editing.split_percentage,
                referral_deduction: editing.referral_deduction, notes: editing.notes,
            });
            setEditing(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Remove this disbursement for ${row.agent_name}?`)) return;
        try { await apiClient.delete(`/api/commissions/${row.disbursement_id}`); load(); }
        catch (err) { setError(err.message); }
    };

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Commission Disbursements</h1>
                <button onClick={openCompute} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Compute from transaction</button>
            </div>
            <p className="text-slate-500 mb-4">Split closed-deal commissions between the brokerage and agents, and pay them — every step posts to the ledger.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}
            {flash && <p className="mb-4 text-green-600">{flash}</p>}

            <div className="bg-white rounded-lg shadow p-3">
                <DataList rows={rows} rowKey="disbursement_id" csvName="commission-disbursements"
                    empty="No disbursements yet — compute one from a closed transaction."
                    filters={[{ key: 'status', label: 'Status' }, { key: 'agent_name', label: 'Agent' }]}
                    defaultSort={{ key: 'disbursement_id', dir: 'desc' }}
                    columns={[
                        { key: 'disbursement_id', label: '#', type: 'number', render: (r) => <span className="text-xs text-slate-400">{r.disbursement_id}</span> },
                        { key: 'agent_name', label: 'Agent', render: (r) => <span className="font-medium text-slate-700">{r.agent_name}</span> },
                        { key: 'property', label: 'Property', value: (r) => r.property_address || `Txn #${r.transaction_id}`, render: (r) => <span className="text-slate-500">{r.property_address || `Txn #${r.transaction_id}`}</span> },
                        { key: 'gross_commission', label: 'Gross', type: 'number', render: (r) => fmt(r.gross_commission) },
                        { key: 'split_percentage', label: 'Split', type: 'number', render: (r) => `${Number(r.split_percentage)}%` },
                        { key: 'agent_amount', label: 'Agent $', type: 'number', render: (r) => <span className="font-medium">{fmt(r.agent_amount)}</span> },
                        { key: 'brokerage_amount', label: 'Brokerage', type: 'number', render: (r) => <span className="text-slate-500">{fmt(r.brokerage_amount)}</span> },
                        { key: 'status', label: 'Status', render: (r) => <span className={`text-xs px-2 py-0.5 rounded ${STATUS[r.status]}`}>{r.status}</span> },
                        { key: 'check_number', label: 'Check #' },
                        { key: 'actions', label: 'Actions', sortable: false, csv: false, search: false, render: (r) => (
                            <span className="whitespace-nowrap">
                                {r.status !== 'paid' && r.status !== 'void' && <>
                                    <button onClick={() => setEditing({ ...r })} className="text-slate-500 hover:underline text-xs mr-2">Edit</button>
                                    {!r.income_gl_entry_id && <button onClick={() => recordIncome(r)} className="text-blue-600 hover:underline text-xs mr-2">Record income</button>}
                                    <button onClick={() => setPaying({ ...r, paid_date: new Date().toISOString().slice(0, 10), check_number: '' })} className="text-green-600 hover:underline text-xs mr-2">Pay</button>
                                    <button onClick={() => remove(r)} className="text-red-500 hover:underline text-xs">×</button>
                                </>}
                                {r.status === 'paid' && <span className="text-xs text-slate-400">{r.check_number ? `#${r.check_number}` : 'paid'}</span>}
                            </span>) },
                    ]} />
            </div>

            {/* Compute modal */}
            <Modal open={!!computing} onClose={() => setComputing(null)} title="Compute commission from a transaction"
                   footer={<>
                       <button onClick={() => setComputing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={runCompute} disabled={isSaving || !computing?.transaction_id}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Computing...' : 'Compute'}
                       </button>
                   </>}>
                {computing && (
                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-slate-700">Transaction</label>
                        <select value={computing.transaction_id} onChange={e => setComputing({ transaction_id: e.target.value })}
                                className="w-full border rounded-md px-3 py-2">
                            <option value="">Select a closed transaction...</option>
                            {transactions.map(t => (
                                <option key={t.transaction_id} value={t.transaction_id}>
                                    #{t.transaction_id} · {t.street_address || t.property_address || 'property'} · {t.final_price ? fmt(t.final_price) : 'no price'} · {t.status}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-slate-500">Gross commission = final price × total commission %. It's split across the transaction's agents, and each agent's share applies their split percentage.</p>
                    </div>
                )}
            </Modal>

            {/* Pay modal */}
            <Modal open={!!paying} onClose={() => setPaying(null)} title={paying ? `Pay ${paying.agent_name} ${fmt(paying.agent_amount)}` : ''}
                   footer={<>
                       <button onClick={() => setPaying(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={doPay} disabled={isSaving}
                               className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50">
                           {isSaving ? 'Paying...' : 'Pay & book'}
                       </button>
                   </>}>
                {paying && (
                    <div className="space-y-3">
                        <div><label className="block text-sm font-medium text-slate-700">Paid date</label>
                            <input type="date" value={paying.paid_date} onChange={e => setPaying(p => ({ ...p, paid_date: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        <div><label className="block text-sm font-medium text-slate-700">Check / reference #</label>
                            <input value={paying.check_number} onChange={e => setPaying(p => ({ ...p, check_number: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        <p className="text-xs text-slate-500">Books Dr Agent Commission Expense / Cr Operating Bank for {fmt(paying.agent_amount)}.</p>
                    </div>
                )}
            </Modal>

            {/* Edit modal */}
            <Modal open={!!editing} onClose={() => setEditing(null)} title="Adjust disbursement"
                   footer={<>
                       <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveEdit} disabled={isSaving} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
                   </>}>
                {editing && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Gross commission</label>
                                <input type="number" step="0.01" value={editing.gross_commission} onChange={e => setEditing(p => ({ ...p, gross_commission: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                            <div><label className="block text-sm font-medium text-slate-700">Agent split %</label>
                                <input type="number" step="0.01" value={editing.split_percentage} onChange={e => setEditing(p => ({ ...p, split_percentage: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Referral deduction (off the top)</label>
                            <input type="number" step="0.01" value={editing.referral_deduction} onChange={e => setEditing(p => ({ ...p, referral_deduction: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        <div><label className="block text-sm font-medium text-slate-700">Notes</label>
                            <input value={editing.notes || ''} onChange={e => setEditing(p => ({ ...p, notes: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CommissionsPage;
